import { get } from "firebase/database";
import { firebase } from '../config/configureFirebase';
import store from '../store/store';
import { getMonthKey } from '../other/networkSlots';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const isCompleted = (b) => b.status === 'PAID' || b.status === 'COMPLETE';

const bookingMonthKey = (b) => {
  const ms = Number(b.tripdate);
  return ms ? getMonthKey(new Date(ms)) : null;
};

/** 'YYYY-MM' → 'Septiembre 2026' */
export const reportMonthLabel = (monthKey) => {
  if (!monthKey) return '—';
  const [y, m] = String(monthKey).split('-').map(Number);
  return MONTH_NAMES[m - 1] ? `${MONTH_NAMES[m - 1]} ${y}` : String(monthKey);
};

/** Reservas para reportes: admin todas, fleetadmin solo las de su flota. Lectura única. */
export const fetchReportBookings = async () => {
  const { bookingRef, bookingListRef } = firebase;
  const profile = store.getState().auth.profile;
  if (!profile?.uid || !profile?.usertype) {
    return [];
  }
  const source = profile.usertype === 'admin' ? bookingRef : bookingListRef(profile.uid, profile.usertype);
  const snapshot = await get(source);
  const data = snapshot.val() || {};
  return Object.keys(data).map((id) => ({ ...data[id], id }));
};

export const buildDriverEarnings = (bookings) => {
  const rows = {};
  bookings.forEach((b) => {
    if (!isCompleted(b) || !b.driver) return;
    const monthKey = bookingMonthKey(b);
    if (!monthKey) return;
    const key = `${monthKey}_${b.driver}`;
    if (!rows[key]) {
      rows[key] = {
        id: key,
        monthKey,
        driverUid: b.driver,
        driverName: b.driver_name || '—',
        vehicleNo: b.vehicle_number || '',
        rides: 0,
        earnings: 0,
      };
    }
    rows[key].rides += 1;
    rows[key].earnings += num(b.driver_share);
    if (b.vehicle_number) rows[key].vehicleNo = b.vehicle_number;
  });
  return Object.values(rows).sort((a, b) => b.monthKey.localeCompare(a.monthKey) || b.earnings - a.earnings);
};

/**
 * Utilidad = comisión de plataforma − descuentos. El cargo por cancelación se le cobra al
 * cliente y se le acredita al conductor, así que no es ingreso de la plataforma.
 */
export const buildPlatformEarnings = (bookings) => {
  const rows = {};
  const rowOf = (monthKey) => {
    if (!rows[monthKey]) {
      rows[monthKey] = {
        id: monthKey,
        monthKey,
        rides: 0,
        cancelledWithFee: 0,
        grossFare: 0,
        customerPaid: 0,
        driverShare: 0,
        platformFee: 0,
        fleetFee: 0,
        discounts: 0,
        cancellationFees: 0,
      };
    }
    return rows[monthKey];
  };
  bookings.forEach((b) => {
    const monthKey = bookingMonthKey(b);
    if (!monthKey) return;
    if (isCompleted(b)) {
      const r = rowOf(monthKey);
      r.rides += 1;
      r.grossFare += num(b.trip_cost);
      r.customerPaid += num(b.customer_paid);
      r.driverShare += num(b.driver_share);
      r.platformFee += num(b.convenience_fees);
      r.fleetFee += num(b.fleetCommission);
      r.discounts += num(b.discount);
    } else if (b.status === 'CANCELLED' && num(b.cancellationFee) > 0) {
      const r = rowOf(monthKey);
      r.cancelledWithFee += 1;
      r.cancellationFees += num(b.cancellationFee);
    }
  });
  return Object.values(rows)
    .map((r) => ({ ...r, profit: r.platformFee - r.discounts }))
    .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
};

export const buildFleetEarnings = (bookings, usersById = {}) => {
  const rows = {};
  bookings.forEach((b) => {
    if (!isCompleted(b) || !b.fleetadmin || num(b.fleetCommission) <= 0) return;
    const monthKey = bookingMonthKey(b);
    if (!monthKey) return;
    const key = `${monthKey}_${b.fleetadmin}`;
    if (!rows[key]) {
      const u = usersById[b.fleetadmin];
      rows[key] = {
        id: key,
        monthKey,
        fleetUid: b.fleetadmin,
        fleetName: u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || b.fleetadmin : b.fleetadmin,
        rides: 0,
        commission: 0,
      };
    }
    rows[key].rides += 1;
    rows[key].commission += num(b.fleetCommission);
  });
  return Object.values(rows).sort((a, b) => b.monthKey.localeCompare(a.monthKey) || b.commission - a.commission);
};

/** Reporte de red por niveles (últimos 12 meses). Lo calcula la Cloud Function network_level_report. */
export const fetchNetworkLevelReport = async () => {
  const { auth, config } = firebase;
  const settings = store.getState().settingsdata.settings;
  const host =
    window && window.location && settings.CompanyWebsite === window.location.origin
      ? window.location.origin
      : `https://${config.projectId}.web.app`;
  try {
    const idToken = await auth.currentUser.getIdToken();
    const response = await fetch(`${host}/network_level_report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({}),
    });
    const result = await response.json();
    if (result.success) return result;
    return { success: false, error: result.error || 'No se pudo generar el reporte.' };
  } catch (error) {
    return { success: false, error: 'No se pudo contactar al servidor. Revisa tu conexión e intenta de nuevo.' };
  }
};
